import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { admAPI } from "../../lib/admApi";
import { toast } from "sonner";
import { School, Send, ArrowRight, Globe, BedDouble, Bus, Sparkles, ChevronDown } from "lucide-react";

/**
 * Public enquiry & campus tour landing page. Path:
 *   /education360/apply/:school/enquire
 * No auth required.
 */
export default function PublicEnquiry() {
  const { school } = useParams();
  const [prospectus, setProspectus] = useState(null);
  const [form, setForm] = useState({
    tenant_slug: school,
    parent_name: "", parent_email: "", parent_phone: "",
    student_name: "", year_group: "Year 7", entry_year: "2026-2027",
    country: "", boarding_interest: false, transport_interest: false,
    tour_requested: false, virtual_meeting: false, message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    admAPI.prospectus(school).then(({ data }) => setProspectus(data)).catch(() => setProspectus({}));
  }, [school]);

  const submit = async () => {
    if (!form.parent_email || !form.parent_name) return toast.error("Name and email are required");
    setBusy(true);
    try {
      await admAPI.submitEnquiry(form);
      setSubmitted(true);
      toast.success("Enquiry received — the admissions team will be in touch shortly.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const tenant = prospectus?.tenant || {};
  const brandBg = `linear-gradient(135deg, ${tenant.branding?.primary_color || "#002147"} 0%, ${tenant.branding?.accent_color || "#D9531E"} 100%)`;

  return (
    <div className="edu360-root min-h-screen bg-[color:var(--hl-page)]" data-testid="public-enquiry">
      {/* Hero */}
      <div className="text-white" style={{ background: brandBg }}>
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-3">
            <School className="w-6 h-6" />
            <div className="text-xs uppercase tracking-[0.28em]">Admissions360™</div>
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
            {tenant.name || "Highlands International College"}
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">{tenant.branding?.motto || "Ad Astra Per Aspera"}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/education360/apply/${school}`}
              className="bg-white text-slate-900 px-5 py-3 rounded-md font-semibold text-sm flex items-center gap-2"
              data-testid="btn-start-application"
            >
              Start an application <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#enquire" className="bg-white/10 border border-white/30 text-white px-5 py-3 rounded-md text-sm">
              Book a campus tour
            </a>
          </div>
        </div>
      </div>

      {/* Programmes */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold mb-2">Programmes</div>
        <h2 className="font-display font-bold text-3xl text-[color:var(--hl-navy)]">A world-class British curriculum</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {(prospectus?.programmes || []).map((p, i) => (
            <div key={i} className="hl-card p-6">
              <div className="text-[color:var(--hl-orange)] text-xs uppercase tracking-[0.22em] font-bold">Programme {i + 1}</div>
              <div className="font-display font-bold text-xl text-slate-800 mt-2">{p.name}</div>
              <div className="text-sm text-slate-600 mt-2">{p.focus}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <FeatureCard icon={BedDouble} title="Boarding" body={`${prospectus?.boarding?.capacity || 240} beds across ${prospectus?.boarding?.houses?.length || 4} historic houses.`} />
          <FeatureCard icon={Bus} title="Transport" body="Daily coach routes across Ascot, Windsor, Reading and Bracknell." />
          <FeatureCard icon={Globe} title="Global community" body="Students from 40+ nationalities. English as an additional language support available." />
        </div>

        {/* Fee estimator */}
        {prospectus?.fee_estimate && (
          <div className="hl-card p-6 mt-10" data-testid="fee-estimate">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold">Indicative fees</div>
            <h3 className="font-display font-bold text-2xl text-[color:var(--hl-navy)] mt-1">From ${(prospectus.fee_estimate.tuition_from || 0).toLocaleString()} tuition · ${(prospectus.fee_estimate.boarding_from || 0).toLocaleString()} boarding (annual)</h3>
            <div className="text-xs text-slate-500 mt-2">{prospectus.fee_estimate.note}</div>
          </div>
        )}
      </div>

      {/* Enquiry form */}
      <div id="enquire" className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold mb-2">Enquire</div>
          <h2 className="font-display font-bold text-3xl text-[color:var(--hl-navy)]">Book a tour or request information</h2>
          <p className="text-slate-500 mt-2">Our admissions team responds within one working day.</p>

          {submitted ? (
            <div className="hl-card p-8 mt-6 text-center border-l-4 border-[color:var(--hl-orange)]" data-testid="enquiry-success">
              <Sparkles className="w-6 h-6 mx-auto text-[color:var(--hl-orange)]" />
              <div className="font-display font-bold text-lg mt-2">Thank you — enquiry received</div>
              <div className="text-sm text-slate-500 mt-1">The Highlands admissions team will be in touch shortly.</div>
              <Link to={`/education360/apply/${school}`} className="hl-button-primary text-xs mt-4 inline-block">Start a formal application</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8" data-testid="enquiry-form">
              <Field label="Parent name" v={form.parent_name} on={v => setForm({ ...form, parent_name: v })} testId="enq-parent-name" />
              <Field label="Parent email" type="email" v={form.parent_email} on={v => setForm({ ...form, parent_email: v })} testId="enq-parent-email" />
              <Field label="Parent phone" v={form.parent_phone} on={v => setForm({ ...form, parent_phone: v })} testId="enq-parent-phone" />
              <Field label="Country" v={form.country} on={v => setForm({ ...form, country: v })} testId="enq-country" />
              <Field label="Student name" v={form.student_name} on={v => setForm({ ...form, student_name: v })} testId="enq-student-name" />
              <SelectField label="Year group" v={form.year_group} on={v => setForm({ ...form, year_group: v })} options={["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"]} testId="enq-year" />

              <div className="md:col-span-2 flex flex-wrap gap-3 text-sm text-slate-700">
                <CheckField label="Boarding interest" v={form.boarding_interest} on={v => setForm({ ...form, boarding_interest: v })} testId="enq-boarding" />
                <CheckField label="Transport interest" v={form.transport_interest} on={v => setForm({ ...form, transport_interest: v })} testId="enq-transport" />
                <CheckField label="Campus tour" v={form.tour_requested} on={v => setForm({ ...form, tour_requested: v })} testId="enq-tour" />
                <CheckField label="Virtual meeting" v={form.virtual_meeting} on={v => setForm({ ...form, virtual_meeting: v })} testId="enq-virtual" />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Message</label>
                <textarea rows={4} className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="enq-message" />
              </div>
              <div className="md:col-span-2">
                <button onClick={submit} disabled={busy} className="hl-button-primary text-sm disabled:opacity-60" data-testid="btn-submit-enquiry">
                  <Send className="w-3.5 h-3.5 inline mr-1" /> {busy ? "Sending…" : "Send enquiry"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold mb-2">FAQ</div>
        <h2 className="font-display font-bold text-3xl text-[color:var(--hl-navy)]">Frequently asked</h2>
        <div className="mt-6 space-y-3">
          {(prospectus?.faq || []).map((f, i) => (
            <details key={i} className="hl-card p-4 group">
              <summary className="cursor-pointer flex items-center justify-between font-semibold text-slate-800 list-none">
                {f.q}
                <ChevronDown className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="text-sm text-slate-600 mt-2">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="hl-card p-6">
      <div className="w-10 h-10 rounded bg-[color:var(--hl-navy)] text-white grid place-items-center mb-3">
        <Icon className="w-4 h-4" />
      </div>
      <div className="font-display font-bold text-lg text-slate-800">{title}</div>
      <div className="text-sm text-slate-600 mt-1">{body}</div>
    </div>
  );
}

function Field({ label, v, on, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <input type={type} value={v || ""} onChange={e => on(e.target.value)} data-testid={testId} className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm" />
    </label>
  );
}

function SelectField({ label, v, on, options, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select value={v || ""} onChange={e => on(e.target.value)} data-testid={testId} className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}

function CheckField({ label, v, on, testId }) {
  return (
    <label className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2 text-sm">
      <input type="checkbox" checked={!!v} onChange={e => on(e.target.checked)} data-testid={testId} />
      {label}
    </label>
  );
}
